import { activateUser, forgotPassword, getContactById } from 'src/services/client';
import { toast } from 'react-toastify';
import { IContactFormData, PropertyDetail } from 'src/SharedCustomHooks/UseContactFormData/types';
import { upload } from 'src/helpers/upLoadHelper';
import { IContactFormDataForAPI } from 'src/utils/CommonHelpers/ContactAPIOperations/types';

const fetchContactForSuiteByIdFromAPI = async (contactId: string, userId: string, suiteId?: string): Promise<any> => {
  try {
    const response: any = await getContactById(contactId, userId, suiteId);
    
    if (response.status === 200 || response.ok) {
      const responseJson = await response.json();
      return responseJson;
    } else {
      throw new Error(`Failed to fetch contact: ${response.status}`);
    }
  } catch (err: any) {
    console.error('Failed to fetch contact by ID:', err);
    toast.error('🚫 Failed to load contact details. Please try again.', {
      autoClose: 4000, position: 'top-center',
    });
    throw err;
  }
};

const handleImageUpload = async (formData: IContactFormData): Promise<string | null> => {
  if (!formData.selectedImage?.length) {
    return formData.profileImage || null;
  }
  
  try {
    return await upload(formData.selectedImage[0]);
  } catch (error) {
    console.error('Image upload failed:', error);
    toast.error('⚠️ Image upload failed, continuing without image.', {
      autoClose: 4000, position: 'top-center',
    });
    return formData.profileImage || null;
  }
};

const transformContactFormDataForAPI = (
  formData: IContactFormData,
  imageUrl: string | null
): IContactFormDataForAPI => {
  const transformedProperties: string[] =
    formData.properties?.map(
      (property: PropertyDetail) => property.propertyId
    ) || [];
  
  const transformed = {
    ...formData,
    profileImage: imageUrl || formData.profileImage,
    properties: transformedProperties,
  };
  
  // Add ServiceProviderAssignedProperties for Service Providers.
  if (formData.userRole === 'Service Provider') {
    const propertyIds =
      formData.properties?.map(
        (property: PropertyDetail) => property.propertyId
      ) || [];
    
    return {
      ...transformed,
      ServiceProviderAssignedProperties: propertyIds,
    };
  }
  
  return transformed;
};

const sendResetPasswordLink = async (
  contactData: IContactFormData
): Promise<void> => {
  try {
    const response: any = await forgotPassword(contactData.email);
    
    if (response.ok || response.status === 200) {
      toast.success(
        ` ⓘ Password reset link has been sent to user ${contactData.firstname} ${contactData.lastname}!`,
        { autoClose: 10000, position: 'top-center' }
      );
    } else {
      toast.error(
        '🚫 Something went wrong on our end. Please try again in a few minutes. If the issue persists, please contact support.',
        {
          autoClose: 4000,
          position: 'top-center',
        }
      );
    }
  } catch (err) {
    console.error('Failed to send password reset link:', err);
    toast.error('🚫 Something went wrong, please try again.', {
      autoClose: 4000,
      position: 'top-center',
    });
  }
};

const sendActivationLink = async (
  contactData: IContactFormData
): Promise<void> => {
  try {
    const response: any = await activateUser({ UserId: contactData.id });
    
    if (response.status === 200 || response.ok) {
      const responseJson = await response.json();
      
      if (responseJson.statusCode === 200) {
        toast.success(
          ` ⓘ Activation link sent to user ${contactData.firstname} ${contactData.lastname}`,
          { autoClose: 10000, position: 'top-center' }
        );
      } else {
        toast.error(
          '🚫 Something went wrong with your request to send an activation email, please try again.',
          {
            autoClose: 4000,
            position: 'top-center',
          }
        );
      }
    } else {
      toast.error('🚫 Something went wrong, please try again.', {
        autoClose: 4000,
        position: 'top-center',
      });
    }
  } catch (err) {
    console.error('Failed to send activation link:', err);
    toast.error(
      '🚫 Something went wrong on our end. Please try again in a few minutes. If the issue persists, please contact support.',
      {
        autoClose: 4000,
        position: 'top-center',
      }
    );
  }
};

const handleContactsAPIResponse = (
  response: any,
  isUpdate: boolean,
  setResponse: (response: any) => void,
  setError: (error: string | null) => void
): boolean => {
  if (response.status === 200) {
    const message = isUpdate
      ? 'Contact has been updated'
      : 'Contact has been added';

    toast.success(`ⓘ ${message}.`, {
      autoClose: 10000,
      position: 'top-center',
    });

    setResponse(response);
    return true;
  }

  if (response.status === 400 || response.status === 409) {
    toast.error(response.message, {
      autoClose: 12000,
      position: 'top-center',
    });
    setError(response.message);
    return false;
  }

  throw new Error(`Unexpected response status: ${response.status}`);
};

export {
  fetchContactForSuiteByIdFromAPI,
  handleImageUpload,
  transformContactFormDataForAPI,
  sendResetPasswordLink,
  sendActivationLink,
  handleContactsAPIResponse
};